import React, { useMemo } from 'react';
import type { DataTableRenderCellParams, DataTableRowEventHandler } from '@/components/custom/erp/data-table-v2';
import type { ButtonType } from '@/components/custom/erp/input-table-v2';
import { EditableTableWithContext } from '../EditableTableWithContext';
import type { RepeatRowsPayload } from '@/components/custom/erp';
import { SelectedCellInfo } from '../hooks/useDetailRows';
import { getDetailTableColumns } from './columns';
import { FormMode } from '@/types/form';

interface DetailTabProps {
  formMode: FormMode;
  rows: { uuid?: string | null }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: DataTableRowEventHandler<MuaHangRecord>;
  onCellClick?: (params: DataTableRenderCellParams<MuaHangRecord>) => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void;
  onRepeatForRows: (payload: RepeatRowsPayload<MuaHangRecord>) => void;
}

const DETAIL_ACTION_ORDER: ButtonType[] = ['delete', 'copy', 'paste', 'moveUp', 'moveDown', 'export', 'pin'];
const noop = () => {};

export const DetailTab: React.FC<DetailTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  onRepeatForRows
}) => {
  const columns = useMemo(() => getDetailTableColumns(onCellValueChange), [onCellValueChange]);
  const tableActions = useMemo(
    () => ({
      delete: onDeleteRow,
      copy: onCopyRow,
      paste: onPasteRow,
      moveUp: () => onMoveRow('up'),
      moveDown: () => onMoveRow('down'),
      export: noop,
      pin: noop
    }),
    [onCopyRow, onDeleteRow, onMoveRow, onPasteRow]
  );

  return (
    <EditableTableWithContext
      formMode={formMode}
      rows={rows}
      onRowClick={onRowClick}
      onCellClick={onCellClick}
      selectedRowId={selectedRowUuid || undefined}
      selectedCell={selectedCell}
      columns={columns}
      onRepeatForRows={onRepeatForRows}
      actions={tableActions}
      actionOrder={DETAIL_ACTION_ORDER}
    />
  );
};

export default DetailTab;
